import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { IconLock } from '@tabler/icons-react';
import { useAuthStore } from '../../lib/auth.js';
import { logoutRequest } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { useOpenPortal, useStartCheckout, type SubscriptionStatus } from '../../hooks/useBilling.js';

/**
 * Full-screen "subscribe to continue" block for a billing-lapsed agency. Shown
 * app-wide by `AppLayout` when the billing-block flag is set (the api interceptor
 * flips it on a 402 PAYMENT_REQUIRED); suspension takes precedence over it. The
 * server independently enforces the block (402 on feature routes) — this is the
 * UX layer.
 *
 * `status` is optional: when omitted (the live block path) it presents the
 * subscribe CTA, which works for every blocked state (none / canceled / unpaid),
 * since Checkout reuses the existing Stripe customer. A caller that knows the
 * tenant has a lapsed (not absent) subscription may pass `status` to offer the
 * customer portal instead.
 */
export function ReactivateScreen({ status }: { status?: SubscriptionStatus }) {
  const role = useAuthStore((s) => s.user?.role);
  const router = useRouter();
  const checkout = useStartCheckout();
  const portal = useOpenPortal();
  const [loggingOut, setLoggingOut] = useState(false);
  const busy = checkout.isPending || portal.isPending || loggingOut;

  const needsCheckout = status === undefined || status === 'none';

  async function logout() {
    setLoggingOut(true);
    await logoutRequest();
    router.navigate({ to: '/login' });
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-navy">
          <IconLock size={22} className="text-gold-soft" />
        </div>
        <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">
          {needsCheckout ? 'Subscribe to continue' : 'Your subscription has lapsed'}
        </h1>
        <p className="text-sm text-muted mb-7">
          {role === 'admin'
            ? needsCheckout
              ? 'Choose a plan to unlock Admitly for your team. You can cancel anytime.'
              : 'Reactivate your subscription to restore access. Your data is safe and waiting.'
            : 'Your agency’s subscription is inactive. Please ask your account admin to subscribe.'}
        </p>

        {role === 'admin' &&
          (needsCheckout ? (
            <div className="flex flex-col gap-2">
              <Button className="w-full" loading={busy} onClick={() => checkout.mutate('monthly')}>
                Subscribe monthly
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                loading={busy}
                onClick={() => checkout.mutate('annual')}
              >
                Subscribe annually
              </Button>
            </div>
          ) : (
            <Button className="w-full" loading={busy} onClick={() => portal.mutate()}>
              Reactivate subscription
            </Button>
          ))}

        {(checkout.isError || portal.isError) && (
          <p className="mt-4 text-sm text-danger">Couldn&rsquo;t reach billing. Please try again.</p>
        )}

        <button
          type="button"
          onClick={logout}
          disabled={busy}
          className="mt-6 text-sm text-muted underline underline-offset-2 hover:text-ink disabled:opacity-50"
        >
          Log out
        </button>
      </div>
    </div>
  );
}
